import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { ShieldAlert, LogOut, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Inactividad total tolerada (ms) antes de cerrar sesión automáticamente.
const IDLE_TIMEOUT_MS = 5 * 60 * 1000;        // 5 minutos
// Cuánto antes del corte mostramos el aviso (ms).
const WARN_BEFORE_MS = 30 * 1000;             // 30 segundos
// Throttle de los listeners de actividad para no spamear timers.
const ACTIVITY_THROTTLE_MS = 1000;

// Eventos que cuentan como "actividad del usuario" según spec Feb 2026:
// movimiento de ratón, clic, scroll y pulsación de tecla (+ touch para móvil).
// NO se cuenta el tráfico de red — la medición es puramente client-side.
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'click', 'keydown', 'scroll', 'wheel', 'touchstart', 'touchmove'];

const SessionTimeoutPopup = () => {
  const { user, logout, refreshToken } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(Math.floor(WARN_BEFORE_MS / 1000));
  // (Feb 2026) Modal "sesión expirada" que sustituye al redirect duro.
  // Se muestra cuando el logout se dispara pero el usuario está viendo
  // contenido sin interactuar — no perdemos la página actual.
  const [showExpired, setShowExpired] = useState(false);

  // Refs para timers para que los listeners no se re-suscriban en cada render.
  const lastActivityRef = useRef(Date.now());
  const warnTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const lastTrackedRef = useRef(0);
  const showWarningRef = useRef(false);

  const clearAllTimers = () => {
    if (warnTimerRef.current) { clearTimeout(warnTimerRef.current); warnTimerRef.current = null; }
    if (logoutTimerRef.current) { clearTimeout(logoutTimerRef.current); logoutTimerRef.current = null; }
    if (countdownTimerRef.current) { clearInterval(countdownTimerRef.current); countdownTimerRef.current = null; }
  };

  const doLogout = useCallback(async () => {
    clearAllTimers();
    setShowWarning(false);
    showWarningRef.current = false;
    // (Feb 2026) NO redirigimos: mostramos un modal "sesión expirada"
    // encima de la pantalla actual para que el usuario no pierda la vista.
    // La cookie se limpia en el backend pero la URL se conserva.
    try { await logout(); } catch { /* noop */ }
    setShowExpired(true);
  }, [logout]);

  // Redirige a la pantalla de login preservando la URL actual como `redirect=`
  // para que la vuelta tras autenticarse sea al mismo activo/pantalla.
  const goToLoginPreservingPath = () => {
    const currentPath = window.location.pathname + window.location.search + window.location.hash;
    // Rutas "safe" (dentro de la app). Evita open redirect.
    const safePath = currentPath.startsWith('/') && !currentPath.startsWith('//') ? currentPath : '/dashboard';
    window.location.href = `/login?reason=inactivity&redirect=${encodeURIComponent(safePath)}`;
  };

  // Reinicia el ciclo: programa warning + logout desde "ahora".
  const scheduleTimers = useCallback(() => {
    clearAllTimers();
    lastActivityRef.current = Date.now();
    warnTimerRef.current = setTimeout(() => {
      setSecondsLeft(Math.floor(WARN_BEFORE_MS / 1000));
      setShowWarning(true);
      showWarningRef.current = true;
      // Countdown visual cada segundo.
      countdownTimerRef.current = setInterval(() => {
        setSecondsLeft((s) => (s > 0 ? s - 1 : 0));
      }, 1000);
    }, IDLE_TIMEOUT_MS - WARN_BEFORE_MS);
    logoutTimerRef.current = setTimeout(() => {
      doLogout();
    }, IDLE_TIMEOUT_MS);
  }, [doLogout]);

  // "Seguir conectado": pide refresh al backend y reinicia el ciclo.
  const handleStay = async () => {
    try {
      await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
    } catch {
      // Si el refresh falla → forzar logout limpio.
      return doLogout();
    }
    setShowWarning(false);
    showWarningRef.current = false;
    if (refreshToken) {
      // Re-sincroniza el usuario en memoria (cookies ya renovadas).
      refreshToken().catch(() => {});
    }
    scheduleTimers();
  };

  const handleLogoutClick = async () => {
    clearAllTimers();
    setShowWarning(false);
    showWarningRef.current = false;
    try { await logout(); } catch { /* noop */ }
    // Logout explícito → redirige limpio al login (sin preservar URL).
    window.location.href = '/login';
  };

  // Listeners de actividad (throttle 1s). Mientras el aviso esté abierto,
  // NO reseteamos automáticamente — el usuario debe pulsar "Seguir conectado".
  useEffect(() => {
    if (!user) return;
    scheduleTimers();

    const onActivity = () => {
      if (showWarningRef.current) return; // congelado mientras el modal está abierto
      const now = Date.now();
      if (now - lastTrackedRef.current < ACTIVITY_THROTTLE_MS) return;
      lastTrackedRef.current = now;
      scheduleTimers();
    };

    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));
    return () => {
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, onActivity));
      clearAllTimers();
    };
  }, [user, scheduleTimers]);

  if (!user && !showExpired) return null;
  if (!showWarning && !showExpired) return null;

  const mm = Math.floor(secondsLeft / 60).toString().padStart(2, '0');
  const ss = (secondsLeft % 60).toString().padStart(2, '0');

  // Modal "sesión expirada" — sobreescribe al warning si aplica.
  if (showExpired) {
    return (
      <div
        className="fixed inset-0 z-[60] flex items-center justify-center p-4"
        style={{ background: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(4px)' }}
        data-testid="session-expired-overlay"
      >
        <div
          className="bg-white rounded-xl shadow-2xl max-w-md w-full"
          data-testid="session-expired-modal"
          role="alertdialog"
          aria-labelledby="session-expired-title"
        >
          <div className="flex items-start gap-3 p-5 border-b border-slate-100">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <ShieldAlert className="w-5 h-5 text-red-600" />
            </div>
            <div className="flex-1">
              <h3 id="session-expired-title" className="font-semibold text-slate-800">
                Tu sesión ha expirado
              </h3>
              <p className="text-sm text-slate-600 mt-1">
                Por seguridad tu sesión se ha cerrado por inactividad.
                Recarga para volver a autenticarte — te llevaremos de
                vuelta a esta misma pantalla.
              </p>
            </div>
          </div>

          <div className="p-4 border-t border-slate-100 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <button
              onClick={goToLoginPreservingPath}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded"
              data-testid="session-expired-reauth-btn"
              autoFocus
            >
              <RefreshCw className="w-4 h-4" />
              Recargar y volver a autenticarme
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(4px)' }}
      data-testid="session-timeout-overlay"
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-md w-full"
        data-testid="session-timeout-modal"
        role="alertdialog"
        aria-labelledby="session-timeout-title"
      >
        <div className="flex items-start gap-3 p-5 border-b border-slate-100">
          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
            <ShieldAlert className="w-5 h-5 text-amber-600" />
          </div>
          <div className="flex-1">
            <h3 id="session-timeout-title" className="font-semibold text-slate-800">
              Tu sesión está a punto de cerrarse
            </h3>
            <p className="text-sm text-slate-600 mt-1">
              Tu sesión se cerrará en <strong>{secondsLeft} segundos</strong> por
              inactividad. ¿Deseas continuar?
            </p>
          </div>
        </div>

        <div className="px-5 py-4 flex flex-col items-center">
          <span className="text-xs text-slate-500 uppercase tracking-wide">Tiempo restante</span>
          <span
            className="text-4xl font-mono font-semibold mt-1"
            style={{ color: secondsLeft <= 10 ? '#dc2626' : '#0f172a' }}
            data-testid="session-timeout-countdown"
          >
            {mm}:{ss}
          </span>
        </div>

        <div className="p-4 border-t border-slate-100 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <button
            onClick={handleLogoutClick}
            className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded border border-slate-200 text-slate-700 hover:bg-slate-50"
            data-testid="session-timeout-logout-btn"
          >
            <LogOut className="w-4 h-4" />
            Cerrar sesión
          </button>
          <button
            onClick={handleStay}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded"
            data-testid="session-timeout-stay-btn"
            autoFocus
          >
            Extender sesión
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionTimeoutPopup;
